// Terminal: chování jako původní Terminal. Přepínače Intraday/Week/Month/Year a výběr
// data mění graf bez znovunačtení stránky (data z HTTP endpointu, URL přes pushState,
// funguje i tlačítko Zpět). V aktuálním období S&P poslouchá živé zprávy přes stávající
// WebSocket ws/terminal/ (consumer se nemění; nic neposíláme, get_latest_candle
// consumer neobsluhuje). Ropa nemá push kanály, v aktuálním období se obnovuje každou minutu.

const PAGE = 50;
const POLL_MS = 60e3;
const store = {
  get(k, d) { try { const v = localStorage.getItem(k); return v === null ? d : v === 'true'; } catch { return d; } },
  set(k, v) { try { localStorage.setItem(k, String(v)); } catch { /* prohlížeč bez úložiště */ } },
};
const pad = (n) => String(n).padStart(2, '0');
const parseDay = (iso) => { const [y, m, d] = iso.split('-').map(Number); return new Date(y, m - 1, d); };

export function terminal(cfg) {
  return {
    cfg,
    tf: cfg.tf,
    day: cfg.day,
    data: null,
    loading: true,
    error: '',
    page: 1,
    showNews: store.get('mx_terminal_news', true),
    sound: store.get('mx_terminal_sound', true),
    conn: 'loading',          // loading | live | connecting | offline | historical | polling
    range: null,
    now: new Date(),
    flash: '',                // up | down – krátké probliknutí ceny
    popup: null,              // nová zpráva vpravo nahoře (60 s)
    detail: null,             // detail zprávy po kliknutí na popisek v grafu
    fresh: new Set(),
    charts: null,
    mod: null,
    ws: null,
    retry: 0,
    pollTimer: null,
    reqId: 0,

    async init() {
      const [mod, hc] = await Promise.all([import('../charts/terminal.js'), import('../charts/highcharts.js')]);
      this.mod = mod;
      this.applyTheme = hc.applyChartTheme;
      setInterval(() => { this.now = new Date(); }, 1000);
      window.addEventListener('mx:themechange', () => this.draw());
      window.addEventListener('popstate', (e) => {
        const s = e.state || this.parsePath();
        if (s) this.load(s.tf, s.day, false);
      });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && this.data?.live && this.data.market.livePush && (!this.ws || this.ws.readyState > 1)) this.connect();
      });
      history.replaceState({ tf: this.tf, day: this.day }, '');
      await this.load(this.tf, this.day, false);
    },

    parsePath() {
      const m = location.pathname.match(/terminal\/(intraday|week|month|year)\/(\d{4}-\d{2}-\d{2})?/);
      return m ? { tf: m[1], day: m[2] || this.day } : null;
    },

    // ---------------------------------------------------------------- načtení období
    async load(tf, day, push = true) {
      const id = ++this.reqId;
      this.loading = true;
      this.error = '';
      this.tf = tf;
      try {
        const res = await fetch(`${cfg.dataUrl}?tf=${tf}&date=${day}`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.status);
        const data = await res.json();
        if (id !== this.reqId) return;          // mezitím přišel novější požadavek
        this.data = data;
        this.day = data.day;
      } catch {
        if (id !== this.reqId) return;
        this.error = 'Terminal data could not be loaded. Try again in a moment.';
        this.loading = false;
        return;
      }
      if (push) history.pushState({ tf, day: this.day }, '', `${cfg.baseUrl}${tf}/${this.day}/`);
      this.page = 1;
      this.range = this.data.defaultRange;
      this.loading = false;
      this.$nextTick(() => this.draw());
      this.setupLive();
    },
    switchTf(tf) { if (tf !== this.tf || !this.data) this.load(tf, this.day); },
    changeDate(value) { if (value) this.load(this.tf, value); },

    // Data stejného období znovu (sentiment z pushe, obnova ropy) – graf zůstane na stejném rozsahu
    async refresh() {
      const id = this.reqId;
      try {
        const res = await fetch(`${cfg.dataUrl}?tf=${this.tf}&date=${this.day}`, { credentials: 'same-origin' });
        if (!res.ok || id !== this.reqId) return;
        const data = await res.json();
        const known = new Set(this.data.news.map((n) => n.id));
        const added = data.news.filter((n) => !known.has(n.id));
        this.data = data;
        added.forEach((n) => { this.fresh.add(n.id); });
        if (added.length) this.notify(added[0]);
        this.draw();
      } catch { /* další pokus při příštím intervalu */ }
    },

    // ---------------------------------------------------------------- grafy
    draw() {
      if (!this.data || !this.mod) return;
      this.applyTheme();
      Object.values(this.charts || {}).forEach((c) => { try { c?.destroy(); } catch { /* již zničený */ } });
      const height = window.innerWidth < 700 ? 520 : 800;
      const main = this.mod.mainChart(this.$refs.main, this.data, { range: this.range, showNews: this.showNews, height,
        onNewsClick: (n) => { this.detail = n; } });
      const minis = this.mod.miniCharts({ wave: this.$refs.wave, trend: this.$refs.trend, vol: this.$refs.vol, fg: this.$refs.fg, tree: this.$refs.tree }, this.data);
      this.charts = { main, ...minis };
    },
    setRange(key) { this.range = key; this.mod.setRange(this.charts.main, this.data, key); },
    toggleNews() {
      this.showNews = !this.showNews;
      store.set('mx_terminal_news', this.showNews);
      this.mod.toggleNews(this.charts.main, this.showNews);
    },
    toggleSound() {
      this.sound = !this.sound;
      store.set('mx_terminal_sound', this.sound);
      if (this.sound) this.beep();
    },
    beep() { try { const a = new Audio(cfg.soundUrl); a.volume = 0.5; a.play().catch(() => {}); } catch { /* bez zvuku */ } },

    // ---------------------------------------------------------------- hlavička
    get clock() {
      const d = this.now;
      return `Now: ${d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} at ${d.toLocaleTimeString('en-US')}`;
    },
    get periodText() {
      const d = parseDay(this.day);
      const long = (x) => x.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
      if (this.tf === 'week') {
        const mon = new Date(d); mon.setDate(d.getDate() - ((d.getDay() + 6) % 7));
        return `${long(mon)} – ${long(d)}`;
      }
      if (this.tf === 'month') return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (this.tf === 'year') return String(d.getFullYear());
      return long(d);
    },
    get periodAgo() {
      if (this.data?.live) return 'live';
      // Vůči dnešnímu kalendářnímu dni (jako dřív „2 days ago“ i o víkendu)
      const d = parseDay(this.day), now = new Date(), t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const days = Math.round((t - d) / 86400000);
      const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'} ago`;
      if (this.tf === 'week') { const w = Math.floor((days + ((d.getDay() + 6) % 7)) / 7); return w <= 0 ? 'this week' : plural(w, 'week'); }
      if (this.tf === 'month') { const m = (t.getFullYear() - d.getFullYear()) * 12 + t.getMonth() - d.getMonth(); return m <= 0 ? 'this month' : plural(m, 'month'); }
      if (this.tf === 'year') { const y = t.getFullYear() - d.getFullYear(); return y <= 0 ? 'this year' : plural(y, 'year'); }
      return days <= 0 ? 'today' : plural(days, 'day');
    },
    get priceText() {
      const c = this.data?.price.close;
      return c == null ? '—' : `${c.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}p`;
    },
    badgeText(key) {
      const v = this.data?.state?.badges?.[key];
      return v == null ? '---' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}p`;
    },
    badgeTone(key) {
      const v = this.data?.state?.badges?.[key] ?? 0, t = cfg.badgeThreshold[key];
      // Nad prahem plná barva, pod prahem jemná (jako bg-success / bg-success-soft dřív)
      return v >= t ? 'pos solid' : v > 0 ? 'pos' : v <= -t ? 'neg solid' : v < 0 ? 'neg' : '';
    },
    stateTone(v) {
      return { RiskOn: 'pos', Growing: 'pos', RiskOff: 'neg', Fading: 'neg' }[v] || '';
    },

    // ---------------------------------------------------------------- feed
    get pages() { return Math.max(1, Math.ceil((this.data?.news.length || 0) / PAGE)); },
    get pageNews() { return (this.data?.news || []).slice((this.page - 1) * PAGE, this.page * PAGE); },
    ago(t) {
      const s = Math.max(0, Math.floor((this.now - t) / 1000));
      return s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m` : s < 86400 ? `${Math.floor(s / 3600)}h` : `${Math.floor(s / 86400)}d`;
    },
    hhmm(t) { const d = new Date(t); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; },
    when(t) { return new Date(t).toLocaleString('en-GB'); },
    signed(v, d = 4) { return (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toFixed(d); },
    tone(v) { return v > 0 ? 'pos' : v < 0 ? 'neg' : ''; },
    // Silná zpráva: |value| > 0.03 v měřítku S&P (ropa násobí cfg.scale)
    level(v) { const t = 0.03 * (cfg.scale || 1); return v > t ? 'lv-sp' : v > 0 ? 'lv-p' : v < -t ? 'lv-sn' : v < 0 ? 'lv-n' : 'lv-0'; },
    newsUrl(n) { return `${cfg.newsUrl}${n.id}/`; },
    topicUrl(n) { return n.slug && n.slug !== 'other' ? `${cfg.topicUrl}${n.slug}/` : null; },
    notify(n) {
      if (this.sound) this.beep();
      this.popup = n;
      clearTimeout(this.popupTimer);
      this.popupTimer = setTimeout(() => { this.popup = null; }, 60e3);
    },

    // ---------------------------------------------------------------- živá data
    setupLive() {
      clearInterval(this.pollTimer);
      if (!this.data.live) {
        if (this.ws) { this.ws.onclose = null; this.ws.close(); this.ws = null; }
        this.conn = 'historical';
        return;
      }
      if (this.data.market.livePush) {
        if (!this.ws || this.ws.readyState > 1) this.connect(); else this.conn = 'live';
      } else {
        this.conn = 'polling';
      }
      // Ropa: svíčky živě přes WebSocket, zprávy a sentiment dotazem na server
      if (!this.data.market.livePush || this.data.market.poll) {
        this.pollTimer = setInterval(() => { if (document.visibilityState === 'visible') this.refresh(); }, POLL_MS);
      }
    },
    connect() {
      this.conn = 'connecting';
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      let ws;
      try { ws = new WebSocket(`${proto}://${location.host}/ws/terminal/?market=${encodeURIComponent(cfg.market)}`); } catch { this.conn = 'offline'; return; }
      this.ws = ws;
      ws.onopen = () => { this.conn = 'live'; this.retry = 0; };
      ws.onmessage = (e) => { try { this.onMessage(JSON.parse(e.data)); } catch { /* neplatná zpráva */ } };
      ws.onclose = () => {
        if (this.ws !== ws) return;
        this.conn = 'offline';
        const wait = Math.min(30000, 1000 * 1.5 ** this.retry++);
        setTimeout(() => { if (this.ws === ws && this.data?.live && document.visibilityState === 'visible') this.connect(); }, wait);
      };
    },

    onMessage(msg) {
      if (!this.data?.live || !msg.channel) return;
      const m = msg.message || {};
      const mk = this.data.market;
      if (msg.channel === mk.newsChannel && m.id) { if (!m.deleted) this.onNews(m); }
      else if (msg.channel === mk.sentimentChannel && m.date === this.data.day) this.onSentiment(m);
      else if (msg.channel === this.data.market.candleChannel) this.onCandle(m);
    },

    onNews(m) {
      const item = { id: String(m.id), t: m.created_at, text: m.text, value: Number(m.value) || 0, topic: m.topic || 'Other', slug: m.topic_slug || 'other' };
      if (item.t < this.data.start || item.t > this.data.end) return;
      const news = this.data.news;
      const i = news.findIndex((n) => n.id === item.id);  // zpráva se posílá i při úpravě – nahradit, ne zdvojit
      if (i >= 0) news.splice(i, 1, item);
      else { news.unshift(item); news.sort((a, b) => b.t - a.t); this.fresh.add(item.id); this.notify(item); }
      const c = this.charts; if (!c) return;
      this.mod.refreshNews(c.main, this.data, { visible: this.showNews, onClick: (n) => { this.detail = n; } });
      c.tree?.series[0]?.setData(this.mod.heatmapPoints(this.data.news, cfg.market));
    },

    onSentiment(m) {
      const s = this.data.state;
      s.sentiment = m.sentiment; s.trend = m.trend; s.index = m.moodix_index;
      s.badges = { intraday: m.moodix_index_intraday, week: m.moodix_index_week, month: m.moodix_index_month, year: m.moodix_index_year };
      // Vlna, trend, volatilita a Fear & Greed se přepočítají ze serveru (jako dřív get_sentiment_data)
      clearTimeout(this.sentTimer);
      this.sentTimer = setTimeout(() => this.refreshMinis(), 1500);
    },
    async refreshMinis() {
      try {
        const res = await fetch(`${cfg.dataUrl}?tf=${this.tf}&date=${this.day}`, { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = await res.json();
        this.data.series30 = data.series30;
        this.data.state = data.state;
        ['wave', 'trend', 'vol', 'fg', 'tree'].forEach((k) => { try { this.charts[k]?.destroy(); } catch { /* již zničený */ } });
        Object.assign(this.charts, this.mod.miniCharts({ wave: this.$refs.wave, trend: this.$refs.trend, vol: this.$refs.vol, fg: this.$refs.fg, tree: this.$refs.tree }, this.data));
      } catch { /* další push to zkusí znovu */ }
    },

    onCandle(m) {
      const tfOf = { '1m': ['intraday'], '10m': ['week', 'month'], '1d': ['year'] }[m.timeframe] || [];
      if (!tfOf.includes(this.data.tf) || !this.data.hasCandles) return;
      const t = m.timestamp ?? Date.parse(m.time.endsWith('Z') || m.time.includes('+') ? m.time : `${m.time}Z`);
      const candle = [t, +m.open, +m.high, +m.low, +m.close];
      const list = this.data.candles;
      if (list.length && list[list.length - 1][0] === t) list[list.length - 1] = candle; else if (!list.length || t > list[list.length - 1][0]) list.push(candle); else return;
      const series = this.charts?.main.get('price');
      const last = series?.points?.length ? series.points[series.points.length - 1] : null;
      if (last && last.x === t) last.update(candle, true); else series?.addPoint(candle, true);
      const p = this.data.price;
      if (p.close != null && candle[4] !== p.close) {
        this.flash = candle[4] > p.close ? 'up' : 'down';
        setTimeout(() => { this.flash = ''; }, 500);
      }
      p.close = candle[4];
      const base = p.prevClose || (list[0] && list[0][1]);
      p.changePct = base ? Math.round((p.close - base) / base * 10000) / 100 : null;
    },
  };
}
